// @ts -nocheck
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
    DragDropContext,
    Draggable,
    DraggingStyle,
    Droppable,
    DroppableProvided,
    DroppableStateSnapshot,
    NotDraggingStyle,
} from 'react-beautiful-dnd'
import { Subscription } from 'rxjs'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { EnvironmentContext } from '../../../../providers/environment/EnvironmentContext'
import { environmentService } from '../../../../providers/environment/EnvironmentService'
import { GuslThemeContext } from '../../../../providers/theme/GuslThemeProvider'
import { RunOnceEffect, unSubscribe } from '../../../../utils/Utils'
import { FieldConfigDTO } from '../../../types'
import { ElementWithTooltip } from '../../element-with/element-with-tooltip/ElementWithTooltip'
import { GuslTableState, reOrderColumn, toggleColumnsSettings, toggleFieldDisplay } from '../guslTableSlice'
import { SidePanelTitle } from '../side-panel/SidePanelTitle'

import { columnSettingsClosed } from '../../../../features/ui/uiSlice'
import { guslStorage } from '../../../../providers/session/GuslStorage'
import {
    ColumnNameStyled,
    DraggableColumnStyled,
    DraggableIconStyled,
    HideShowColumnStyled,
    TickStyled,
} from '../styled/columns-settings/styled'
import {
    CascadingColumnSettingsBackdropStyled,
    CascadingColumnSettingsDrawerStyled,
    FilterTypeWrapperStyled,
    TableSidePanelWrapperStyled,
} from '../styled/side-panel/styled'

// const {DragDropContext, Draggable, Droppable } = window.ReactBeautifulDnd;

type ColumnsSettingsProps = {
    code: string
}
export default function ColumnsSettings({ code }: ColumnsSettingsProps) {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [className] = useState('ColumnsSettings-' + new Date().getTime())
    const title = 'Column Settings'

    const dispatch = useAppDispatch()
    const state: GuslTableState = useAppSelector((state) => state.guslTableSlice[code])
    const guslThemeContext = React.useContext(GuslThemeContext)
    const environmentContext = React.useContext(EnvironmentContext)
    const isMobile = environmentContext.isMobileDevice()

    const [lastIdx, setLastIdx] = useState<number>(1000)
    const [grid] = useState<number>(2)
    const displayElement = useRef(null)
    const [bodyHeight, setBodyHeight] = useState(600)

    const [panelBgColor] = useState<string>(
        guslThemeContext.getCurrentTheme(environmentService.getEnvironment()?.storagePrefix).panel.panelBgColor
    )
    const [movingBgColor] = useState<string>(
        guslThemeContext.getCurrentTheme(environmentService.getEnvironment()?.storagePrefix).navigation.sideMenu.hoverBgColor
    )
    // const [movingBgColor] = useState<string>(guslThemeContext.getCurrentTheme(environmentService.getEnvironment()?.storagePrefix).colors.primary[1]);
    const [movingFgColor] = useState<string>(
        guslThemeContext.getCurrentTheme(environmentService.getEnvironment()?.storagePrefix).navigation.sideMenu.hoverColor
    )
    const [itemBgColor] = useState<string>(
        guslThemeContext.getCurrentTheme(environmentService.getEnvironment()?.storagePrefix).navigation.sideMenu.bgColor
    )
    const [itemFgColor] = useState<string>(
        guslThemeContext.getCurrentTheme(environmentService.getEnvironment()?.storagePrefix).navigation.sideMenu.color
    )
    const [itemFontSize] = useState<string>('14px')
    const [border] = useState<string>(guslThemeContext.getCurrentTheme(environmentService.getEnvironment()?.storagePrefix).form.fieldBorder)

    const [footerHeight, setFooterHeight] = useState<number>(0)
    RunOnceEffect(() => {
        let heightSubscription: Subscription = environmentService.watchFooterHeight().subscribe((height: number) => {
            setFooterHeight(height)
        })
        return () => {
            unSubscribe(heightSubscription)
        }
    })

    const extractCodePattern = (str: string): string => {
        const firstNumberIndex = str.search(/\d/)

        if (firstNumberIndex === -1) {
            return str
        }

        const result = str.substring(0, firstNumberIndex)
        const cleanResult = result.endsWith('_') ? result.slice(0, -1) : result
        return cleanResult
    }

    useEffect(() => {
        window.requestAnimationFrame(function () {
            setTimeout(() => {
                if (displayElement) {
                    // @ts-ignore
                    const rect = displayElement?.current?.getBoundingClientRect()
                    if (rect) {
                        let h = window.innerHeight - rect.top
                        if (isMobile) {
                            h = h - footerHeight - 3
                        } else {
                            h = h - 20
                        }
                        setBodyHeight(h > 0 ? h : 500)
                    } else {
                        setBodyHeight(500)
                    }
                }
            }, 100)
        })
    }, [code])
    const updateHiddenColumnsOnServer = (field: FieldConfigDTO, properties?: any) => {
        state.sessionContext
            ?.post('/preferences/column-toggle', {
                code: extractCodePattern(code),
                field: field.name,
                properties: {
                    columns: properties,
                },
            })
            .then(() => {})
            .catch(() => {
                // ignore
            })
    }

    const updateDisplayOrderListOnServer = (firstIdx: number, lastIdx: number) => {
        state.sessionContext
            ?.post('/preferences/column-order', {
                code: code,
                firstIdx: firstIdx,
                lastIdx: lastIdx,
            })
            .then(() => {})
            .catch(() => {
                // ignore
            })
    }

    function columnsHandler(field: FieldConfigDTO) {
        dispatch(toggleFieldDisplay({ code, field }))
        let reducedFields = {}
        if (state) {
            const orderedFields = guslStorage.getOrderedFields(state?.code, state?.allFields)
            reducedFields = orderedFields
                .filter((field) => field.displayInTable)
                .map((field) => ({ name: field.name, displayInTable: field.displayInTable }))
        }
        console.log('reducedFields', reducedFields)
        updateHiddenColumnsOnServer(field, reducedFields)
    }

    const getListStyle = (isDraggingOver: boolean) => ({
        background: isDraggingOver ? panelBgColor : panelBgColor,
        padding: grid,
        width: '100%',
    })

    const getItemStyle = (draggableStyle: DraggingStyle | NotDraggingStyle | undefined, isDragging: boolean) => ({
        userSelect: 'none',
        padding: grid * 2,
        margin: `0 0 ${grid}px 0`,
        background: isDragging ? movingBgColor : itemBgColor,
        color: isDragging ? movingFgColor : itemFgColor,
        fontSize: itemFontSize,
        borderBottom: border,
        lineHeight: environmentContext.isMobileDevice() ? '30px' : '18px',
        ...draggableStyle,
    })

    const onDragEnd = (result: any) => {
        // dropped outside the list
        if (!result.destination) {
            return
        }
        dispatch(
            reOrderColumn({
                code,
                firstIdx: result.source.index,
                lastIdx: result.destination.index,
            })
        )
        updateDisplayOrderListOnServer(result.source.index, result.destination.index)
        setLastIdx(1000)
    }

    const renderDraggableItem = (field: FieldConfigDTO, index: number): React.ReactElement => {
        return (
            <HideShowColumnStyled active={lastIdx === index}>
                <ElementWithTooltip
                    element={
                        <TickStyled
                            className={'px-2'}
                            active={field.displayInTable}
                            onClick={() => {
                                columnsHandler(field)
                            }}
                        >
                            {field.displayInTable ? <>&#x2713;</> : <>&#x2717;</>}
                        </TickStyled>
                    }
                    tooltip={
                        <span>
                            {field.displayInTable ? 'Hide' : 'Show'} {field.label}
                        </span>
                    }
                />

                <DraggableColumnStyled key={index} role={'button'} active={lastIdx === index} className={'d-flex justify-content-between'}>
                    <div>
                        <ColumnNameStyled active={field.displayInTable}>{field.label}</ColumnNameStyled>
                    </div>

                    <ElementWithTooltip
                        element={
                            <DraggableIconStyled
                                icon={'fa-solid fa-up-down-left-right '}
                                className={'pt-1 pe-1'}
                                active={field.displayInTable || false}
                            />
                            // </div>
                        }
                        tooltip={<span>Drag up or down to re-order columns.</span>}
                    />
                </DraggableColumnStyled>
            </HideShowColumnStyled>
        )
    }

    const renderDraggableItems = (): React.ReactElement => {
        return (
            <>
                {state.allFields.map((field, index) => (
                    <Draggable key={field.name} draggableId={field.name} index={index}>
                        {(provided: any, snapshot: any) => (
                            <div>
                                <div
                                    ref={provided.innerRef}
                                    {...provided.dragHandleProps}
                                    {...provided.draggableProps}
                                    style={getItemStyle(provided.draggableProps.style, snapshot.isDragging)}
                                >
                                    {renderDraggableItem(field, index)}
                                </div>
                                {provided.placeholder}
                            </div>
                        )}
                    </Draggable>
                ))}
            </>
        )
    }

    const closeColumnsSettings = () => {
        dispatch(toggleColumnsSettings({ code: code }))
        if (state.showColumnsSettings) {
            dispatch(columnSettingsClosed())
        }
    }

    const renderDragDropList = (fillHeight: boolean): React.ReactElement => {
        return (
            <>
                <SidePanelTitle title={title} onCloseClick={closeColumnsSettings} />
                <FilterTypeWrapperStyled ref={displayElement} height={fillHeight ? undefined : bodyHeight} style={fillHeight ? { flex: 1 } : undefined}>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
                                <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)} {...provided.droppableProps}>
                                    {renderDraggableItems()}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </FilterTypeWrapperStyled>
            </>
        )
    }

    const renderDraggableList = (): React.ReactElement => {
        // MK: react-beautiful-dnd doesn't support a draggable list nested inside
        // an already-scrollable ancestor (a cascading table's parent row body is
        // scrollable), so render it as a fixed drawer via a portal instead.
        if (state.isCascadingTable) {
            return createPortal(
                <>
                    <CascadingColumnSettingsBackdropStyled onClick={closeColumnsSettings} />
                    <CascadingColumnSettingsDrawerStyled onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        {renderDragDropList(true)}
                    </CascadingColumnSettingsDrawerStyled>
                </>,
                document.body
            )
        }

        return <TableSidePanelWrapperStyled>{renderDragDropList(false)}</TableSidePanelWrapperStyled>
    }

    return (
        // <>{renderOriginal()}</>
        <>{renderDraggableList()}</>
    )
}
